'use client';

import { useQuery } from '@tanstack/react-query';
import Select, {
  type MultiValue,
  type ValueContainerProps,
  components,
} from 'react-select';
import GuestProductCards from './GuestProductCards';
import { getGuestAccessories } from '../../service/getGuestAccessories';
import { useState } from 'react';
import type {
  GuestNonScriptRangeProductsProps,
  ReactSelectOptionProp,
} from '../../interface/interface';
import showToast from '../ToastComponent';
import { getGuestProductBrands } from '../../service/getGuestProductBrands';
import Loading from '../Loading';
import { ProductBrands } from '../../constant/constant';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ValueContainer = ({ children, ...props }: ValueContainerProps<any>) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let [values, input] = children as any;

  if (Array.isArray(values)) {
    const plural = values.length === 1 ? '' : 's';
    values = `${values.length} item${plural} selected`;
  }

  return (
    <components.ValueContainer {...props}>
      {values}
      {input}
    </components.ValueContainer>
  );
};

export function GuestNonScriptRangeProducts(
  props: GuestNonScriptRangeProductsProps,
) {
  const [brands, setBrands] = useState<MultiValue<ReactSelectOptionProp>>([]);
  const [selectedBrand, setSelectedBrand] = useState<
    Array<ReactSelectOptionProp>
  >([]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['guest-accessories', selectedBrand],
    queryFn: async () => {
      try {
        const accessories = await getGuestAccessories(selectedBrand);

        if (!accessories) {
          showToast({
            message: 'Unable to fetch accessories!',
            type: 'info',
          });
          return [];
        }

        return accessories;
      } catch (error) {
        console.error('Error fetching guest accessories:', error);
        showToast({
          message: 'Error fetching accessories!',
          type: 'error',
        });
        return [];
      }
    },
    retry: 1,
  });

  const { isLoading: isBrandLoading } = useQuery({
    queryKey: ['guest-productBrand'],
    queryFn: async () => {
      try {
        const result = await getGuestProductBrands();

        if (typeof result === 'number') {
          showToast({
            message: 'Unable to fetch product brand!',
            type: 'error',
          });
          return null;
        }

        const filteredBrands = result
          .filter(
            (x: { product_brand: string }) =>
              x.product_brand !== ProductBrands.ATLUS,
          )
          .map((e: { product_brand: string }) => ({
            label: e.product_brand,
            value: e.product_brand,
          }));

        setBrands(filteredBrands);
        return null;
      } catch (error) {
        console.error('Error fetching guest product brands:', error);
        showToast({
          message: 'Error fetching product brands!',
          type: 'error',
        });
        return null;
      }
    },
    retry: 1,
  });

  if (isLoading || isBrandLoading || props.loading) return <Loading />;

  if (error) {
    console.error('Query error:', error);
    return <p>Error loading accessories</p>;
  }

  return (
    <div className="result-block-container">
      <div className="flex justify-start md:justify-end">
        <div className="flex items-center mb-5">
          <small>Brand</small>
          <Select
            classNames={{
              control: (state) =>
                `${
                  state.isFocused
                    ? 'border-grey-400 border text-sm'
                    : 'border-grey-400 border text-sm'
                } w-60 cursor-pointer ml-2`,
            }}
            value={selectedBrand}
            options={brands}
            onChange={(selected) => {
              setSelectedBrand([...selected]);
            }}
            isSearchable
            hideSelectedOptions={false}
            isMulti
            isClearable
            components={{ ValueContainer }}
            isLoading={isBrandLoading}
            placeholder="Select..."
          />
        </div>
      </div>

      <ul className="grid grid-cols-1 gap-6 sm:grid-cols-1 md:grid-cols-3 lg:grid-cols-4">
        {data && data?.length > 0 ? (
          data?.map((product: any, index: number) => (
            <div key={index}>
              <GuestProductCards
                addProductToCart={async (id: string) => {
                  await props.addToCartMutation.mutateAsync(id);
                }}
                removeProductFromCart={async (productId: string) => {
                  await props.removeFromCartMutation.mutateAsync(productId);
                }}
                product={product}
                substitueCard={false}
                isAccessories={true}
                allowedProduct={false}
              />
            </div>
          ))
        ) : (
          <p>No Non-Script Range Products Exist</p>
        )}
      </ul>
    </div>
  );
}
